import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import audioManager from '../audio/audioManager';
import { cardsLabel } from '../game/cards';
import {
  buildMoveAnnouncements,
  startAnnouncement,
  buildMoveAnnouncementsMulti,
  startAnnouncementMulti,
  turnText,
} from './onlineTelecronaca';
import { isMulti } from './multiLabels';

const KEY = { A: 'player', B: 'cpu' };

// Drives the ONLINE telecronaca with the exact same single-channel aria-live
// queue as the offline CPU game (see useScopaGame.js). The queue code below is a
// verbatim copy so the TTS/accessibility behaviour is identical: sequential
// commit with a 1600ms cadence, zero-width toggle for repeated messages, keyed
// supersede ('table'/'turn') and a safety cap that drops the oldest STATE items.
// The only difference from offline is the event SOURCE: server-forwarded events
// on the authoritative view, deduped by monotonic `version` so a reconnect /
// re-sync never re-announces anything.
export default function useOnlineNarration(view, settings) {
  const [live, setLive] = useState('');
  const liveQueue = useRef([]);
  const liveTimer = useRef(null);
  const liveNonce = useRef(0);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const viewRef = useRef(view);
  viewRef.current = view;

  const flushLive = useCallback(() => {
    if (liveTimer.current) return;
    const step = () => {
      const item = liveQueue.current.shift();
      if (item === undefined) {
        liveTimer.current = null;
        return;
      }
      liveNonce.current += 1;
      setLive(item.text + (liveNonce.current % 2 ? '\u200B' : ''));
      liveTimer.current = setTimeout(step, 1600);
    };
    step();
  }, []);

  const pushLive = useCallback(
    (text, key) => {
      if (!text) return;
      if (key) {
        liveQueue.current = liveQueue.current.filter((m) => m.key !== key);
      }
      liveQueue.current.push({ text, key: key || null });
      const MAX = 6;
      while (liveQueue.current.length > MAX) {
        const idx = liveQueue.current.findIndex((m) => m.key);
        if (idx === -1) break;
        liveQueue.current.splice(idx, 1);
      }
      flushLive();
    },
    [flushLive]
  );

  useEffect(
    () => () => {
      clearTimeout(liveTimer.current);
      liveTimer.current = null;
    },
    []
  );

  const telecronaca = useCallback(
    (text, key) => {
      const s = settingsRef.current;
      if (!s.accessibleMode || !s.narrationEnabled) return;
      pushLive(text, key);
    },
    [pushLive]
  );

  const status = useCallback(
    (text, key) => {
      pushLive(text, key);
    },
    [pushLive]
  );

  const readTable = useCallback(() => {
    const v = viewRef.current;
    const t = v ? v.table : [];
    const text = t.length === 0 ? 'Il tavolo è vuoto.' : `Sul tavolo ci sono ${cardsLabel(t)}.`;
    liveQueue.current = [];
    clearTimeout(liveTimer.current);
    liveTimer.current = null;
    liveNonce.current += 1;
    setLive(text + (liveNonce.current % 2 ? '\u200B' : ''));
  }, []);

  const runItems = useCallback(
    (items) => {
      for (const it of items) {
        if (it.sound && typeof audioManager[it.sound] === 'function') audioManager[it.sound]();
        if (it.text) (it.channel === 'telecronaca' ? telecronaca : status)(it.text, it.key);
        if (it.toast) {
          const { kind, title, description } = it.toast;
          if (kind === 'success') toast.success(title, { description });
          else toast(title, { description });
        }
      }
    },
    [telecronaca, status]
  );

  const lastVersion = useRef(null);
  const prevPhase = useRef(null);
  const startAnnounced = useRef(false);
  const lastGame = useRef(null);

  useEffect(() => {
    const v = view;
    if (!v) return;
    // Dedupe: only narrate when the authoritative version advances. A reconnect /
    // re-sync returns the current version with NO events -> nothing re-announced.
    if (lastVersion.current != null && v.version <= lastVersion.current) {
      prevPhase.current = v.phase;
      return;
    }
    const myKey = KEY[v.you];

    // Rematch: a new gameNumber means a brand-new match -> announce its start.
    const gameNo = v.gameNumber || 1;
    if (lastGame.current != null && gameNo !== lastGame.current) {
      startAnnounced.current = false;
      liveQueue.current = []; // drop any leftover narration from the previous match
    }
    lastGame.current = gameNo;

    if (!startAnnounced.current) {
      startAnnounced.current = true;
      const s = isMulti(v) ? startAnnouncementMulti(v) : startAnnouncement(v);
      if (s) telecronaca(s);
      // 3-4 players: rejoining mid-game gets a short recap of whose turn it is.
      else if (isMulti(v) && v.phase === 'playing') status(`Sei di nuovo in partita. ${turnText(v)}`, 'turn');
    }

    // New hand dealt after a handOver pause — verbatim port of continueNextHand.
    if (prevPhase.current === 'handOver' && v.phase === 'playing') {
      if (isMulti(v)) status(`Nuova mano. ${turnText(v)}`);
      else status(v.turn === v.you ? 'Nuova mano. È il tuo turno.' : "Nuova mano. È il turno dell'avversario.");
    }

    if (v.events && v.events.length) {
      runItems(
        isMulti(v)
          ? buildMoveAnnouncementsMulti({ events: v.events, view: v })
          : buildMoveAnnouncements({ events: v.events, myKey, view: v })
      );
    }

    prevPhase.current = v.phase;
    lastVersion.current = v.version;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view && view.version]);

  return { live, status, readTable };
}
